import React, { FC, ReactElement } from 'react';
import classNames from 'classnames';

import { Label, TextInputProps } from '../Text';
import styles from './styles.module.css';
import { useUniqueId } from 'Shared/hooks/useUniqueId/useUniqueId';
import { isPresent } from 'Shared/utils';
import classnames from 'classnames';

export type RadioButtonGroupProps = {
  disabled?: boolean;
  className?: string;
  name: string;
  label?: string;
  hideLabel?: boolean;
  value: string;
  options: { [value: string]: string };
  onChange(value: string): void;
  sizeVariant?: 'small' | 'medium' | 'large' | 'extraLarge';
  other?: { [value: string]: ReactElement };
  radioGroupClassName?: string;
  tooltipLabel?: React.ReactNode;
  tooltipClassName?: string;
} & Omit<
  TextInputProps,
  'onChange' | 'onClick' | 'onFocus' | 'defaultValue' | 'value'
>;

export const RadioButtonGroup: FC<RadioButtonGroupProps> = ({
  disabled,
  value,
  label,
  hideLabel,
  options,
  onChange,
  className,
  name,
  sizeVariant = 'medium',
  errorMessage,
  other,
  tooltip,
  tooltipPosition,
  radioGroupClassName,
  tooltipLabel,
  tooltipClassName,
  ...restOfInputProps
}) => {
  const labelId = `${name}_${label}`;
  const { uniqueId } = useUniqueId();

  return (
    <div
      aria-labelledby={label ? labelId : ''}
      role="group"
      className={classNames(
        styles.wrapper,
        { [styles.disabled]: disabled },
        className
      )}
    >
      {label && (
        <Label
          htmlFor={labelId}
          disabled={disabled}
          className={hideLabel ? styles.hideLabel : ''}
          tooltip={tooltip}
          tooltipPosition={tooltipPosition}
          tooltipLabel={tooltipLabel}
          tooltipClassName={tooltipClassName}
        >
          {label}
        </Label>
      )}
      <div
        className={classNames(radioGroupClassName, styles.group, {
          [styles.extraLarge]: sizeVariant === 'extraLarge',
          [styles.large]: sizeVariant === 'large',
          [styles.medium]: sizeVariant === 'medium',
          [styles.small]: sizeVariant === 'small',
          [styles.hasError]: isPresent(errorMessage),
        })}
      >
        {Object.entries(options).map(([optionValue, optionLabel]) => {
          const id = `${name}_${optionValue}_${uniqueId}`;
          return (
            <div className={styles.item} key={id}>
              <input
                type="radio"
                className={styles.radio}
                name={name}
                value={optionValue}
                id={id}
                disabled={disabled}
                checked={value === optionValue}
                onChange={() => {
                  onChange(optionValue);
                }}
                {...restOfInputProps}
              />
              <label
                htmlFor={id}
                key={optionValue}
                data-testid={`${name ?? 'buttonGroup'}-${optionValue}`}
                className={classnames(styles.label, {
                  [styles.hasOther]: isPresent(other),
                })}
              >
                <div>{optionLabel}</div>
                {other && other[optionValue]}
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );
};
